import { useMemo } from "react";
import {
  Box,
  Card,
  CardBody,
  Text,
  Heading,
  Progress,
  Link,
  useColorModeValue,
  useDisclosure,
  Skeleton,
  IconButton,
} from "@chakra-ui/react";
import { Feature, Geometry } from "geojson";
import { Link as RouterLink } from "react-router-dom";

import { SceneFeature } from "../../../types/scenes";
import { formatDate, getSortedSpecies, whaleSpecies } from "../../../utils";
import {
  Overline,
  SceneThumbnail,
  SceneStatusTag,
  SceneOptionsMenu,
  DeleteSceneDialog,
  ShareSceneDialog,
} from "../../../components";
import { SpeciesCount, SpeciesValidatedCount } from "../../../utils/inferenceStats";
import { CollecticonUsers } from "@devseed-ui/collecticons-chakra";

type Props = {
  scene: Feature<Geometry, SceneFeature>;
  inferencesLoading: boolean | undefined;
  species: SpeciesCount;
  speciesValidated: SpeciesValidatedCount;
  setHoveredSceneId: (id: string | null) => void;
  refetchScene?: () => void;
};

function SceneCard({
  scene,
  inferencesLoading,
  species,
  speciesValidated,
  setHoveredSceneId,
  refetchScene,
}: Props) {
  const { s3_prefix: id, date, model_runs, is_shared: isShared, shared_with: collaborators, role } = scene.properties;

  const topSpecies = useMemo(() => {
    return getSortedSpecies(species);
  }, [species]);

  // Whale-only totals, so the validation progress bar reflects only what a
  // user actually needs to validate rather than every detection class
  const whaleValidation = useMemo(() => {
    return Object.entries(speciesValidated)
      .filter(([specie]) => whaleSpecies.includes(specie.split("-")[0]))
      .reduce((acc, [, counts]) => ({
        total: acc.total + counts.total,
        validated: acc.validated + counts.validated
      }), { total: 0, validated: 0 });
  }, [speciesValidated]);

  const hasSuccessModelRuns = model_runs.some(
    ({ status }) => status === "succeeded"
  );
  const latestModelRunFailed = ["pansharpening failed", "inferencing failed"].includes(model_runs[0]?.status);
  const cardBgColor = useColorModeValue("base.50", "base.400");
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();

  const {
    isOpen: isShareOpen,
    onOpen: onShareOpen,
    onClose: onShareClose,
  } = useDisclosure();
  return (
    <>
      <Card
        direction="row"
        overflow="hidden"
        position="relative"
        transition="all 0.24s ease"
        _hover={{ bg: cardBgColor, transform: "translateY(-2px)" }}
        _focus={{ boxShadow: "outline" }}
        onMouseEnter={() => setHoveredSceneId(id)}
        onMouseLeave={() => setHoveredSceneId(null)}
        zIndex="1"
      >
        <Box width="80px" height="80px">
          <SceneThumbnail sceneId={id} width={82} height={82} brightness={1} contrast={1} />
        </Box>
        <CardBody display="flex" flexDir="column" gap={1} p={2.5} px={4}>
          <Box display="flex" flexDir="column" gap={1} height="100%">
            <Box display="flex" gap="1" position="absolute" zIndex="100" isolation="isolate" top="2" right="2" cursor="pointer" onClick={(e) => e.stopPropagation()}>
              {(isShared || role === "collaborator") &&
                <IconButton
                  size="xs"
                  ml="auto"
                  rounded="full"
                  variant="base-text"
                  bg="primary.200a"
                  title={role === "collaborator" ? "Scene was shared with you" : "Scene is shared"}
                  icon={<CollecticonUsers size="3" />}
                  aria-label={role === "collaborator" ? "Scene was shared with you" : "Share Scene"}
                />}
              <Skeleton isLoaded={!inferencesLoading}>
                <SceneOptionsMenu
                  sceneDate={date}
                  sceneId={id}
                  onDeleteScene={onDeleteOpen}
                  onShareScene={onShareOpen}
                  userRole={role}
                />
              </Skeleton>
            </Box>
            <Link
              as={RouterLink}
              to={`/scenes/${id}`}
              mb="auto"
              _hover={{ textDecoration: "none" }}
              _before={{
                content: "''",
                cursor: "inherit",
                display: "block",
                position: "absolute",
                top: 0,
                left: 0,
                zIndex: 0,
                width: "100%",
                height: "100%",
              }}
            >
              <Heading as="h5" fontSize="lg" lineHeight="1" display="flex" gap="1" alignItems="baseline">
                {formatDate(new Date(Date.parse(date))).split(",")[0]}
                <Box as="span" display="inline" opacity="0.8" lineHeight="1" fontSize="md" fontWeight="400">{formatDate(new Date(Date.parse(date))).split(",")[1]}</Box>
              </Heading>
            </Link>
            {inferencesLoading ? (
              Array.from(Array(2)).map((i, ind) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={ind} display="flex" gap="1">
                  <Skeleton height="2" width="3" />
                  <Skeleton height="2" width="20" />
                </Box>
              ))
            ) : topSpecies.length > 0 ? (
              <Box display="flex" gap="4">
                {topSpecies.map(([label, value]) => (
                  <Box
                    key={label}
                    flex="initial"
                    lineHeight="1"
                    as="dl"
                    display="flex"
                    gap="1"
                  >
                    <Overline as="dt" fontWeight="bold">
                      {value}
                    </Overline>{" "}
                    <Overline as="dd">{label.split("-")[0]}</Overline>
                  </Box>
                ))}
              </Box>
            ) : hasSuccessModelRuns ? (
              <Overline>
                <Text display="inline" fontWeight="bold">
                  0{" "}
                </Text>
                Species Detected
              </Overline>
            ) : latestModelRunFailed && (
              <Overline color="red.500">Retry model</Overline>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            gap="2"
            flex="1"
            height="100%"
            justifyContent="center"
            alignItems="center"
          >
            <SceneStatusTag scene={scene} />
            {whaleValidation.total > 0 && (
              <Box textAlign="center" width="100%">
                <Text fontSize="xs" color="base.200">
                  {`Validated (${whaleValidation.validated} / ${whaleValidation.total})`}
                </Text>
                <Progress
                  value={inferencesLoading ? 0 : (whaleValidation.validated / whaleValidation.total) * 100}
                  width="100%"
                  colorScheme="primary"
                  size="sm"
                  borderRadius="4"
                />
              </Box>
            )}
          </Box>
        </CardBody>
      </Card>
      {isDeleteOpen && <DeleteSceneDialog isOpen={isDeleteOpen} onClose={onDeleteClose} sceneId={id} />}
      {isShareOpen && (
        <ShareSceneDialog
          isOpen={isShareOpen}
          onClose={onShareClose}
          sceneId={id}
          collaborators={collaborators || undefined}
          refetchScene={refetchScene}
          role={role}
        />
      )}
    </>
  );
}

export default SceneCard;
